import {
  type FieldPath,
  type FieldValues,
  useFormContext,
} from 'react-hook-form';

import { Switch } from '@/modules/ui/components/switch/Switch';

import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from './Forms';

interface FormSwitchProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> {
  name: TName;
  label: string;
  description?: string;
  disabled?: boolean;
  checkedLabel?: string;
  uncheckedLabel?: string;
  required?: boolean;
}

export function FormSwitch<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  name,
  label,
  description,
  disabled,
  checkedLabel,
  uncheckedLabel,
  required,
}: FormSwitchProps<TFieldValues, TName>) {
  const { control } = useFormContext<TFieldValues>();

  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem>
          <div className="flex flex-col gap-4">
            <FormLabel required={required}>{label}</FormLabel>
            <div className="flex items-center gap-3">
              <FormControl>
                <Switch
                  checked={field.value}
                  onCheckedChange={field.onChange}
                  disabled={disabled}
                />
              </FormControl>
              <span className="text-sm text-neutral-600">
                {field.value ? checkedLabel || 'On' : uncheckedLabel || 'Off'}
              </span>
            </div>
          </div>
          {description && <FormDescription>{description}</FormDescription>}
          <FormMessage />
        </FormItem>
      )}
    />
  );
}
